import { motion } from "framer-motion";

function Connector({ delay = 0 }) {
  return (
    <div className="relative mx-auto h-10 w-px bg-white/10" aria-hidden="true">
      <motion.span
        className="absolute left-1/2 h-2 w-2 -translate-x-1/2 bg-[#e01e37]"
        style={{ boxShadow: "0 0 12px 2px rgba(224,30,55,0.6)" }}
        initial={{ top: "-8px", opacity: 0 }}
        animate={{ top: ["-8px", "40px"], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 1.8, delay, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}

/*
  Reusable technical architecture diagram.
  Props: layers = [{ label, nodes: [{ label, sub }] }]
  Renders a vertical stack of labeled layers with node boxes,
  connected by animated signal connectors — matching the CashFUX tech aesthetic.
*/
export default function ArchitectureDiagram({ layers = [], caption }) {
  return (
    <div className="relative border border-white/10 bg-[#0a0c12] p-6 sm:p-8 lg:p-10" data-testid="architecture-diagram">
      {/* corner ticks */}
      <span className="pointer-events-none absolute left-0 top-0 h-4 w-4 border-l border-t border-white/20" />
      <span className="pointer-events-none absolute right-0 top-0 h-4 w-4 border-r border-t border-white/20" />
      <span className="pointer-events-none absolute left-0 bottom-0 h-4 w-4 border-l border-b border-white/20" />
      <span className="pointer-events-none absolute right-0 bottom-0 h-4 w-4 border-r border-b border-white/20" />

      <div className="mb-6 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
          Architektur
        </span>
        <span className="font-mono text-[10px] text-[#e01e37]">● live</span>
      </div>

      <div className="flex flex-col items-stretch">
        {layers.map((layer, li) => (
          <div key={li}>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: li * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="border border-white/10 bg-[#05060a] p-4 sm:p-5"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
                {layer.label}
              </span>
              <div className="mt-3 flex flex-wrap gap-px bg-white/10 border border-white/10">
                {layer.nodes.map((node, ni) => (
                  <div
                    key={ni}
                    className="group flex min-w-[120px] flex-1 flex-col bg-[#0a0c12] px-4 py-3 transition-colors duration-300 hover:bg-[#12141d]"
                  >
                    <span className="font-display text-sm font-bold tracking-tight text-white transition-colors duration-300 group-hover:text-[#ff3b52]">
                      {node.label}
                    </span>
                    {node.sub && (
                      <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-white/35">
                        {node.sub}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
            {li < layers.length - 1 && <Connector delay={li * 0.3} />}
          </div>
        ))}
      </div>

      {caption && (
        <p className="mt-6 font-mono text-[11px] text-white/35">{caption}</p>
      )}
    </div>
  );
}
